/**
 * Zoom arithmetic specific to the expanded Mermaid diagram view: its bounds,
 * its label, and the scroll offsets that stand in for a transform here. The
 * gesture arithmetic it shares with every other zoomable surface lives in
 * `lib/zoom`. Pure so all of it can be tested without a browser.
 */

import { zoomBySteps, type ZoomBounds } from '../../lib/zoom';

/** Below this the diagram is smaller than the fit-to-width default. */
export const MIN_ZOOM = 0.5;
/** Past this a flowchart is a few nodes filling the screen; further is noise. */
export const MAX_ZOOM = 4;
/** One click of zoom. Geometric, so the steps feel even at every scale. */
export const ZOOM_FACTOR = 1.25;
/** Fit-to-width: the diagram is exactly as wide as the viewport it sits in. */
export const DEFAULT_ZOOM = 1;

const BOUNDS: ZoomBounds = { min: MIN_ZOOM, max: MAX_ZOOM };

/**
 * One zoom step in `direction` (1 in, -1 out), clamped to the bounds and
 * rounded so the label reads `156%` rather than `156.25%`.
 */
export function zoomBy(zoom: number, direction: 1 | -1): number {
  return Math.round(zoomBySteps(zoom, direction, ZOOM_FACTOR, BOUNDS) * 100) / 100;
}

/**
 * The same step, sized by a wheel gesture rather than a click. Unlike `zoomBy`
 * the result is not rounded: a trackpad emits a stream of few-pixel deltas,
 * and rounding each one to a whole percent would quantize them all back to no
 * change at all.
 */
export function zoomByWheelSteps(zoom: number, steps: number): number {
  return zoomBySteps(zoom, steps, ZOOM_FACTOR, BOUNDS);
}

/**
 * The scroll offset that keeps the content under `focal` — an offset from the
 * viewport's leading edge — in place while the layout box grows by `ratio`.
 *
 * This is the scroll-space twin of the translate-space focal math a transform
 * based viewer uses: the content sits at `scroll + focal` before the change
 * and at `(scroll + focal) * ratio` after it, so the offset has to absorb the
 * difference. Callers clamp nothing: the browser already pins scroll offsets
 * to the scrollable range.
 */
export function focalScroll(scroll: number, focal: number, ratio: number): number {
  return (scroll + focal) * ratio - focal;
}

/** `1.25` → `125%`. */
export function formatZoom(zoom: number): string {
  return `${Math.round(zoom * 100)}%`;
}
